import React, { useEffect, useMemo, useState } from "react";
import { listarClientesTodos } from "../services/api";
import { Save, Search } from "lucide-react";
import SeletorModulosLicenca from "./common/SeletorModulosLicenca";
import CenteredAlert, { extrairMensagemErro } from "./common/CenteredAlert";
import { montarItensModulos, quantidadeModuloPorCodigo } from "../utils/modulosLicenca";

const CadastroPdv = ({
  token,
  initialData = {},
  onSubmit,
  isEdit = false,
  licencaId = null,
  successMessage,
  onSaveSuccess,
}) => {
  const [clientes, setClientes] = useState([]);
  const [clientesOriginais, setClientesOriginais] = useState([]);

  const [loadingClientes, setLoadingClientes] = useState(true);
  const [error, setError] = useState(null);

  const [busca, setBusca] = useState("");

  const [pdv, setPdv] = useState({
    idCliente: "",
    descricao: "",
    codigoInstalacao: "",
    ...initialData,
  });
  const [selecaoModulos, setSelecaoModulos] = useState({
    selectedIds: [],
    quantidades: {},
    catalogo: [],
  });
  const [alerta, setAlerta] = useState(null);
  const [salvando, setSalvando] = useState(false);

  // ✅ garante que no edit o idCliente do initialData entre no state
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setPdv((prev) => ({
        ...prev,
        idCliente: String(
          initialData.idCliente ?? initialData.IdCliente ?? prev.idCliente ?? ""
        ),
        descricao: initialData.descricao ?? initialData.Descricao ?? prev.descricao ?? "",
        codigoInstalacao:
          initialData.codigoInstalacao ??
          initialData.CodigoInstalacao ??
          prev.codigoInstalacao ??
          "",
      }));
    }
  }, [initialData]);

  // ✅ puxa clientes no mesmo formato do ZenTag (pageSize/sortBy)
  useEffect(() => {
    const fetchClientes = async () => {
      setLoadingClientes(true);
      setError(null);

      try {
        const all = await listarClientesTodos(token, {
          pageSize: 2000,
          sortBy: "razaosocial",
        });

        const arr = Array.isArray(all) ? all : [];
        setClientesOriginais(arr);
        setClientes(arr);
      } catch (err) {
        console.error("Erro ao carregar clientes:", err);
        setClientesOriginais([]);
        setClientes([]);
        setError("Erro ao carregar clientes.");
      } finally {
        setLoadingClientes(false);
      }
    };

    fetchClientes();
  }, [token]);

  // ✅ busca igual ZenTag (filtra local)
  const handleBuscar = (e) => {
    const termo = e.target.value;
    setBusca(termo);

    const t = termo.toLowerCase();
    const base = Array.isArray(clientesOriginais) ? clientesOriginais : [];

    const filtered = base.filter((c) =>
      (c.cnpj || "").toLowerCase().includes(t) ||
      (c.razaoSocial || "").toLowerCase().includes(t) ||
      (c.nomeFantasia || "").toLowerCase().includes(t)
    );

    setClientes(filtered);
  };

  const clienteSelecionado = useMemo(() => {
    const idNum = Number(pdv.idCliente);
    const base = Array.isArray(clientesOriginais) ? clientesOriginais : [];
    return base.find((c) => c.id === idNum) || null;
  }, [pdv.idCliente, clientesOriginais]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPdv((prev) => ({ ...prev, [name]: value }));
  };

  const qtdLicencas = useMemo(
    () => 1 + quantidadeModuloPorCodigo(selecaoModulos, "ADD_PDV_ADICIONAL"),
    [selecaoModulos]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = isEdit
      ? {
          Descricao: pdv.descricao,
          CodigoInstalacao: pdv.codigoInstalacao,
          QtdLicencas: qtdLicencas,
        }
      : {
          IdCliente: Number(pdv.idCliente),
          Descricao: pdv.descricao,
          CodigoInstalacao: pdv.codigoInstalacao,
          QtdLicencas: qtdLicencas,
        };

    try {
      setSalvando(true);
      await onSubmit(payload, token, {
        itensModulos: montarItensModulos({ ...selecaoModulos, licencaTipo: "ZenPDV" }),
      });

      const msg = successMessage || (isEdit ? "PDV atualizado com sucesso!" : "PDV cadastrado com sucesso!");
      setAlerta({
        variant: "success",
        message: msg,
        onClose: () => {
          setAlerta(null);
          onSaveSuccess?.();
        },
      });
    } catch (error) {
      console.error("Erro ao salvar PDV:", error);
      setAlerta({
        variant: "error",
        message: extrairMensagemErro(error, "Erro ao salvar PDV."),
        onClose: () => setAlerta(null),
      });
    } finally {
      setSalvando(false);
    }
  };

  const lockCliente = isEdit === true;

  return (
    <>
      <CenteredAlert
        show={!!alerta}
        variant={alerta?.variant}
        message={alerta?.message}
        onClose={alerta?.onClose}
      />

      <div className="max-w-3xl mx-auto">
      <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
        {/* header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            {isEdit ? "Editar ZenPDV Desktop" : "Cadastro ZenPDV Desktop"}
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-300/80">
            {isEdit
              ? "Edite os dados da licença ZenPDV Desktop (Windows). O cliente não pode ser alterado."
              : "Selecione um cliente e configure a licença ZenPDV Desktop (1 terminal incluso)."}
          </p>
        </div>

        {/* seletor cliente (igual ZenTag) */}
        <div className="zen-card rounded-2xl p-4 mb-6 dark:bg-white/5 dark:border-white/10">
          {error && <div className="mb-3 text-sm text-red-500">{error}</div>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Buscar cliente por CNPJ / Razão / Nome Fantasia"
                value={busca}
                onChange={handleBuscar}
                className="
                  pl-10 pr-4 py-2.5 w-full rounded-xl border outline-none transition
                  zen-card text-gray-900 placeholder:text-gray-400
                  focus:ring-2 focus:ring-blue-500/60
                  disabled:opacity-60 disabled:cursor-not-allowed
                  dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
                  dark:focus:ring-blue-400/40
                "
                disabled={loadingClientes || lockCliente}
              />
            </div>

            <select
              name="idCliente"
              value={pdv.idCliente}
              onChange={handleChange}
              required={!isEdit}
              className="
                w-full p-2.5 rounded-xl border outline-none transition
                zen-card border-gray-300 text-gray-900
                focus:ring-2 focus:ring-blue-500/60
                disabled:opacity-60 disabled:cursor-not-allowed
                dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                dark:focus:ring-blue-400/40
              "
              disabled={loadingClientes || lockCliente}
            >
              {!lockCliente && (
                <option value="">
                  {loadingClientes ? "Carregando clientes..." : "-- Selecione um cliente --"}
                </option>
              )}

              {/* fallback no edit se ainda não carregou a lista */}
              {lockCliente && pdv.idCliente && !clienteSelecionado && (
                <option value={pdv.idCliente}>Cliente #{pdv.idCliente}</option>
              )}

              {(Array.isArray(clientes) ? clientes : []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.razaoSocial || c.nomeFantasia || "Cliente"} {c.cnpj ? `(${c.cnpj})` : ""}
                </option>
              ))}
            </select>
          </div>

          {clienteSelecionado && (
            <div className="mt-3 text-xs text-gray-500 dark:text-slate-300/70">
              Selecionado:{" "}
              <span className="font-semibold text-gray-700 dark:text-slate-200">
                {clienteSelecionado.nomeFantasia || clienteSelecionado.razaoSocial}
              </span>
              {clienteSelecionado.cnpj ? ` • ${clienteSelecionado.cnpj}` : ""}
              {lockCliente ? <span className="ml-2">• (travado)</span> : null}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* dados do pdv */}
          <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Descrição
                </label>
                <input
                  type="text"
                  name="descricao"
                  value={pdv.descricao}
                  onChange={handleChange}
                  required
                  className="
                    w-full px-3 py-2.5 rounded-xl border outline-none transition
                    zen-card border-gray-300 text-gray-900
                    focus:ring-2 focus:ring-blue-500/60
                    dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                    dark:focus:ring-blue-400/40
                  "
                  placeholder="ex: PDV Loja 01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Código de Instalação
                </label>
                <input
                  type="text"
                  name="codigoInstalacao"
                  value={pdv.codigoInstalacao}
                  onChange={handleChange}
                  required
                  className="
                    w-full px-3 py-2.5 rounded-xl border outline-none transition
                    zen-card border-gray-300 text-gray-900
                    focus:ring-2 focus:ring-blue-500/60
                    dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                    dark:focus:ring-blue-400/40
                  "
                  placeholder="ex: 123456789"
                />
              </div>
            </div>

            <p className="mt-3 text-xs text-gray-500 dark:text-slate-400">
              Terminais licenciados: <span className="font-semibold">{qtdLicencas}</span>
              {" "}(1 base{qtdLicencas > 1 ? ` + ${qtdLicencas - 1} adicional${qtdLicencas > 2 ? "is" : ""}` : ""})
            </p>
          </div>

          <SeletorModulosLicenca
            token={token}
            licencaTipo="ZenPDV"
            licencaId={licencaId}
            selectedIds={selecaoModulos.selectedIds}
            quantidades={selecaoModulos.quantidades}
            onChange={(sel) =>
              setSelecaoModulos((prev) => ({
                ...prev,
                ...sel,
                catalogo: sel.catalogo?.length ? sel.catalogo : prev.catalogo,
              }))
            }
          />

          {/* actions */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loadingClientes || salvando}
              className="
                inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition
                bg-blue-600 text-white hover:bg-blue-500
                disabled:opacity-60 disabled:cursor-not-allowed
              "
            >
              <Save size={18} />
              {salvando ? "Salvando..." : isEdit ? "Salvar Alterações" : "Cadastrar ZenPDV Desktop"}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default CadastroPdv;
